import { useT } from '@/i18n';
import type { OrderRound } from '@restaurant/api-client';
import { List, ListItem, Popover } from 'framework7-react';
import { forwardRef, type MutableRefObject } from 'react';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type PopoverInstance = { el: HTMLElement | null; f7Popover: () => any };

interface RoundActionsProps {
  round: OrderRound | null;
  onClose: () => void;
  onFire: (round: OrderRound) => void;
  onReady: (round: OrderRound) => void;
  onServe: (round: OrderRound) => void;
  onReopen: (round: OrderRound) => void;
  onCancel: (round: OrderRound) => void;
}

/**
 * Contextual round-status transitions, surfaced from the round's status chip -
 * the same quick-action popover the reservation list uses. Staff have full
 * rights over the table, so the menu always offers the sensible moves for the
 * current state, including reverting a served round back to the kitchen.
 */
const RoundActions = forwardRef<PopoverInstance, RoundActionsProps>(function RoundActions(
  { round, onClose, onFire, onReady, onServe, onReopen, onCancel },
  ref,
) {
  const t = useT().tableOrdering.roundActions;

  const renderContent = () => {
    if (!round) return null;
    const r = round;

    const item = (label: string, color: string, icon: string, onClick: () => void) => (
      <ListItem
        key={label}
        title={label}
        link="#"
        style={{ color }}
        onClick={(e: React.MouseEvent) => {
          e.preventDefault();
          onClose();
          onClick();
        }}
      >
        <i slot="media" className="f7-icons" style={{ color, fontSize: 22 }}>
          {icon}
        </i>
      </ListItem>
    );

    const list = (items: React.ReactNode) => (
      <List noChevron style={{ minWidth: 220, margin: 0 }}>
        {items}
      </List>
    );

    // Intent colours, matching the reservation menu: positive moves green,
    // destructive red, reversals orange.
    const fireColor = '#248a3d';
    const serveColor = '#248a3d';
    const reopenColor = '#ff9500';
    const cancelColor = '#ff3b30';

    switch (r.status) {
      case 'PLACED':
        return (
          <>
            {list(<>{item(t.acceptFire, fireColor, 'flame_fill', () => onFire(r))}</>)}
            {list(<>{item(t.cancelRound, cancelColor, 'xmark_circle', () => onCancel(r))}</>)}
          </>
        );
      case 'FIRED':
        return (
          <>
            {list(<>{item(t.markReady, serveColor, 'flame', () => onReady(r))}</>)}
            {list(<>{item(t.markServed, serveColor, 'checkmark_seal_fill', () => onServe(r))}</>)}
          </>
        );
      case 'READY':
        return (
          <>
            {list(<>{item(t.markServed, serveColor, 'checkmark_seal_fill', () => onServe(r))}</>)}
            {list(
              <>
                {item(t.sendBackKitchen, reopenColor, 'arrow_counterclockwise', () => onReopen(r))}
              </>,
            )}
          </>
        );
      case 'SERVED':
        return list(
          <>{item(t.sendBackKitchen, reopenColor, 'arrow_counterclockwise', () => onReopen(r))}</>,
        );
      default:
        return null;
    }
  };

  return (
    <Popover
      ref={ref as MutableRefObject<PopoverInstance>}
      onPopoverClosed={onClose}
      style={{ minWidth: 220 }}
    >
      {renderContent()}
    </Popover>
  );
});

export default RoundActions;
